"use client";

import { InputHTMLAttributes, forwardRef } from "react";

type InputSize = "sm" | "md" | "lg";
type InputVariant = "default" | "legalPad";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  inputSize?: InputSize;
  variant?: InputVariant;
  fullWidth?: boolean;
}

const sizeStyles: Record<InputSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-base",
  lg: "h-12 px-5 text-lg",
};

const variantStyles: Record<InputVariant, string> = {
  default:
    "bg-[var(--color-surface)] border-2 border-[var(--color-border)] focus:border-[var(--color-pad-line-blue)] focus:ring-4 focus:ring-[var(--color-pad-line-blue)]/20",
  legalPad:
    "bg-[var(--color-pad-yellow)] border-2 border-[var(--color-pad-line-blue)] focus:border-[var(--color-pad-line-blue)] focus:ring-4 focus:ring-[var(--color-pad-line-blue)]/30 shadow-[inset_0_2px_4px_rgba(0,0,0,0.05)]",
};

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    hint,
    leftIcon,
    rightIcon,
    inputSize = "md",
    variant = "default",
    fullWidth = true,
    className = "",
    id,
    disabled,
    ...props
  },
  ref
) {
  const inputId =
    id || `input-${label?.toLowerCase().replace(/\s/g, "-") || "field"}`;

  const baseStyles =
    "rounded-lg transition-all duration-200 outline-none placeholder:text-[var(--color-text-muted)] text-[var(--color-text-primary)]";

  return (
    <div className={fullWidth ? "w-full" : ""}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
        >
          {label}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
            {leftIcon}
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          className={`w-full ${baseStyles} ${sizeStyles[inputSize]} ${variantStyles[variant]} ${leftIcon ? "pl-10" : ""} ${rightIcon ? "pr-10" : ""} ${error ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : ""} ${disabled ? "opacity-50 cursor-not-allowed bg-neutral-100" : ""} ${className}`}
          {...props}
        />

        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
            {rightIcon}
          </div>
        )}
      </div>

      {(error || hint) && (
        <p
          className={`mt-1.5 text-sm ${error ? "text-red-600" : "text-[var(--color-text-muted)]"}`}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

export { Input, type InputProps };
